import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import SignaturePad from 'react-signature-canvas';
import styled from 'styled-components';
import Button from '../../../../../../../../react-components/Button';
import { Text } from '../../../../../../../../react-components/Text';

import DeleteIcon from '../../../../../../../../assets/svg/DeleteIcon';
import { DRIVERS_LAYOUT_ID } from '../../../../../../../../components/DriversLayout';

export interface ModalProps {
  open: boolean;
  signature?: string;
  id?: string;
  onClose: () => void;
  onConfirm: () => void;
  onDelete: () => void;
  portalElementId?: string;
  sigPad?: React.RefObject<SignaturePad>;
  onUpdateSignature: (signature: string) => void;
}

const SignatureContainer = styled.div`
  position: relative;
  height: 78%;
  .signature-pad {
    width: calc(100% - 4px);
    height: 100%;
    border: 1px solid ${RoutalPalette.grey4};
    border-radius: 4px;
  }
`;

const generateRandomId = () => `mobile-modal-${Math.random()}`;

export const SignatureBox = ({
  open,
  signature,
  id,
  onConfirm,
  onClose,
  onDelete,
  portalElementId = DRIVERS_LAYOUT_ID,
  sigPad,
  onUpdateSignature,
}: PropsWithChildren<ModalProps>) => {
  const keyRef = useRef<string>(id ?? generateRandomId());

  useEffect(() => {
    if (keyRef.current === null && keyRef.current !== id) {
      keyRef.current = id ?? generateRandomId();
    }
  }, [id]);

  if (!open) return null;

  const content = (
    <Grid
      key={keyRef.current}
      style={{
        height: `100%`,
        width: `100%`,
        backgroundColor: RoutalPalette.backgroundPrimary,
        position: `absolute`,
        inset: 0,
        zIndex: 1009,
        display: `flex`,
        flexWrap: `wrap`,
        justifyContent: `center`,
        alignItems: `center`,
      }}
      className="animate__animated animate__fadeIn animate__faster"
    >
      <div
        style={{
          width: `100%`,
          height: `100%`,
          zIndex: 1000,
          backgroundColor: RoutalPalette.white,
        }}
      >
        <Grid
          container
          direction="column"
          wrap="nowrap"
          justifyContent="space-between"
          style={{
            height: `100%`,
            padding: `12px`,
          }}
        >
          <SignatureContainer>
            {signature && (
              <Grid
                onClick={() => onDelete()}
                style={{
                  fontWeight: `normal`,
                  display: `flex`,
                  flexDirection: `row`,
                  alignItems: `center`,
                  alignSelf: `end`,
                  position: `absolute`,
                  right: 0,
                  padding: `10px`,
                }}
              >
                <DeleteIcon style={{ height: `15px`, color: RoutalPalette.error.medium, width: `15px` }} />
                <Text
                  style={{
                    marginLeft: `4px`,
                    color: RoutalPalette.error.medium,
                    fontSize: `13px`,
                    fontWeight: `normal`,
                  }}
                  intlId="driver.stop.report.remove_signature_new_version"
                />
              </Grid>
            )}
            <SignaturePad
              canvasProps={{ className: `signature-pad` }}
              ref={sigPad}
              onEnd={async () => {
                if (sigPad?.current) {
                  const newSignature = (sigPad.current as any).getTrimmedCanvas().toDataURL(`image/png`);
                  onUpdateSignature(newSignature);
                }
              }}
            ></SignaturePad>
          </SignatureContainer>

          <Grid container direction="column">
            <Button
              variant="default"
              onClick={() => {
                if (signature) {
                  onConfirm();
                }
              }}
              style={{
                height: `40px`,
                fontSize: `18px`,
                fontWeight: `normal`,
              }}
              intlId="driver.stop.report.continue_signature"
            />

            <VSpacer small />

            <Button
              onClick={() => onClose()}
              style={{
                height: `40px`,
                fontSize: `18px`,
                fontWeight: `normal`,
                backgroundColor: `transparent`,
                color: RoutalPalette.grey1,
              }}
              intlId="cancel"
            />
          </Grid>
        </Grid>
      </div>
    </Grid>
  );

  const element = document.getElementById(portalElementId ?? ``);
  if (element) {
    return createPortal(content, element as HTMLElement);
  }

  return content;
};
